/* ==========================================================================
   BlueWoodday — global behaviour
   Vanilla custom elements. No framework, no dependencies.
   ========================================================================== */

(function () {
  'use strict';

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* --- helpers ------------------------------------------------------------ */

  function trapFocus(container, initial) {
    const focusable = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (!focusable.length) return function () {};

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    (initial || first).focus({ preventScroll: true });

    return function release() {
      container.removeEventListener('keydown', onKeydown);
    };
  }

  let lockCount = 0;

  function lockScroll() {
    lockCount += 1;
    document.body.classList.add('is-locked');
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.body.classList.remove('is-locked');
  }

  function formatMoney(cents) {
    const format = window.theme.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  window.theme = window.theme || {};
  window.theme.formatMoney = formatMoney;

  /* --- Drawer -------------------------------------------------------------
     A single element handles cart, search and mobile navigation.
     Any button with [data-drawer-open="id"] toggles it.
  ------------------------------------------------------------------------- */

  class ThemeDrawer extends HTMLElement {
    connectedCallback() {
      this.releaseFocus = null;
      this.opener = null;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
        }
      });

      const overlay = this.querySelector('.drawer__overlay');
      if (overlay) overlay.addEventListener('click', () => this.close());

      this.onKeydown = (event) => {
        if (event.key === 'Escape') this.close();
      };
    }

    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.opener = opener || null;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      document.addEventListener('keydown', this.onKeydown);

      const panel = this.querySelector('.drawer__panel') || this;
      const autofocus = this.querySelector('[data-drawer-autofocus]');
      // Wait for the panel transition to start so focus does not jump the page.
      requestAnimationFrame(() => {
        this.releaseFocus = trapFocus(panel, autofocus);
      });

      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);

      if (this.releaseFocus) {
        this.releaseFocus();
        this.releaseFocus = null;
      }
      if (this.opener) this.opener.focus({ preventScroll: true });

      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    toggle(opener) {
      if (this.classList.contains('is-open')) {
        this.close();
      } else {
        this.open(opener);
      }
    }
  }

  customElements.define('theme-drawer', ThemeDrawer);

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.getAttribute('data-drawer-open'));
    if (!drawer) return;
    event.preventDefault();
    drawer.open(trigger);
  });

  /* --- Header ------------------------------------------------------------- */

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.openItem = null;

      this.onScroll = () => {
        const y = window.scrollY;
        this.classList.toggle('is-stuck', y > 8);
        this.lastY = y;
      };
      this.onScroll();
      window.addEventListener('scroll', this.onScroll, { passive: true });

      // Mega menu: hover on pointer devices, click/keyboard everywhere.
      this.querySelectorAll('[data-mega-parent]').forEach((item) => {
        const trigger = item.querySelector('[data-mega-trigger]');
        if (!trigger) return;

        item.addEventListener('mouseenter', () => this.openMega(item));
        item.addEventListener('mouseleave', () => this.closeMega(item));

        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          if (item.classList.contains('is-open')) {
            this.closeMega(item);
          } else {
            this.openMega(item);
          }
        });

        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) this.closeMega(item);
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.querySelectorAll('[data-mega-parent].is-open').forEach((item) => this.closeMega(item));
      });
    }

    openMega(item) {
      this.querySelectorAll('[data-mega-parent].is-open').forEach((other) => {
        if (other !== item) this.closeMega(other);
      });
      item.classList.add('is-open');
      const trigger = item.querySelector('[data-mega-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      this.classList.add('has-mega-open');
    }

    closeMega(item) {
      item.classList.remove('is-open');
      const trigger = item.querySelector('[data-mega-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (!this.querySelector('[data-mega-parent].is-open')) {
        this.classList.remove('has-mega-open');
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  customElements.define('site-header', SiteHeader);

  /* --- Mobile navigation: nested panels ----------------------------------- */

  document.addEventListener('click', function (event) {
    const toggle = event.target.closest('[data-nav-toggle]');
    if (!toggle) return;
    event.preventDefault();
    const item = toggle.closest('[data-nav-item]');
    if (!item) return;
    const expanded = item.classList.toggle('is-expanded');
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  });

  /* --- Quantity input ----------------------------------------------------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const step = button.dataset.qtyStep === 'up' ? 1 : -1;
          const min = parseInt(this.input.min || '1', 10);
          const next = Math.max(min, (parseInt(this.input.value, 10) || min) + step);
          if (next === parseInt(this.input.value, 10)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  customElements.define('quantity-input', QuantityInput);

  /* --- Cart --------------------------------------------------------------- */

  const Cart = {
    /**
     * Ask Shopify to re-render our cart sections alongside the mutation, so the
     * drawer, the cart page and the header count all update from one request.
     * https://shopify.dev/docs/api/ajax/reference/cart
     */
    sectionsToRender() {
      const ids = [];
      document.querySelectorAll('[data-cart-section]').forEach((el) => {
        const id = el.dataset.cartSection;
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
      return ids;
    },

    async request(url, body) {
      const sections = this.sectionsToRender();
      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (!response.ok) throw data;
      if (data.sections) this.renderSections(data.sections);
      this.announce(data);
      return data;
    },

    renderSections(sections) {
      Object.keys(sections).forEach((id) => {
        const html = new DOMParser().parseFromString(sections[id], 'text/html');
        document.querySelectorAll('[data-cart-section="' + id + '"]').forEach((target) => {
          const source = html.querySelector('[data-cart-section="' + id + '"]');
          if (source) target.innerHTML = source.innerHTML;
        });
      });
    },

    announce(data) {
      const count = typeof data.item_count === 'number' ? data.item_count : null;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data, count: count } }));
    },

    add(formData) {
      const body = {};
      formData.forEach((value, key) => {
        if (key === 'id') {
          body.id = value;
        } else if (key === 'quantity') {
          body.quantity = parseInt(value, 10);
        } else if (key.indexOf('properties[') === 0) {
          body.properties = body.properties || {};
          body.properties[key.slice(11, -1)] = value;
        } else if (key === 'selling_plan') {
          body.selling_plan = value;
        }
      });
      return this.request(window.theme.routes.cartAdd, { items: [body] });
    },

    change(line, quantity) {
      return this.request(window.theme.routes.cartChange, { line: line, quantity: quantity });
    }
  };

  window.theme.Cart = Cart;

  /* Add to cart ------------------------------------------------------------ */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.button = this.querySelector('[data-add-to-cart]');
      this.errorEl = this.querySelector('[data-form-error]');

      this.form.addEventListener('submit', async (event) => {
        // Let the browser handle it when the drawer is disabled.
        if (this.dataset.ajax === 'false') return;
        event.preventDefault();
        if (this.button && this.button.getAttribute('aria-disabled') === 'true') return;

        this.setLoading(true);
        this.showError('');

        try {
          await Cart.add(new FormData(this.form));
          const drawer = document.getElementById('CartDrawer');
          if (drawer) drawer.open();
        } catch (error) {
          this.showError(error.description || error.message || '');
        } finally {
          this.setLoading(false);
        }
      });
    }

    setLoading(loading) {
      if (!this.button) return;
      this.button.classList.toggle('is-loading', loading);
      this.button.disabled = loading;
      const label = this.button.querySelector('[data-add-label]');
      if (!label) return;
      if (loading) {
        this.button.dataset.previousLabel = label.textContent;
        label.textContent = window.theme.strings.adding;
      } else if (this.button.dataset.previousLabel) {
        label.textContent = this.button.dataset.previousLabel;
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }
  }

  customElements.define('product-form', ProductForm);

  /* Cart line item quantity ------------------------------------------------ */

  document.addEventListener('change', async function (event) {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input) return;
    const line = parseInt(input.dataset.line, 10);
    const quantity = parseInt(input.value, 10);
    input.disabled = true;
    try {
      await Cart.change(line, quantity);
    } finally {
      input.disabled = false;
    }
  });

  document.addEventListener('click', async function (event) {
    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    await Cart.change(parseInt(remove.dataset.line, 10), 0);
  });

  /* Header cart count ------------------------------------------------------ */

  document.addEventListener('cart:updated', function (event) {
    const count = event.detail.count;
    if (count === null) return;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  });

  /* --- Predictive search --------------------------------------------------- */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      if (!this.input || !this.results) return;

      this.controller = null;
      this.timer = null;

      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(this.input.value.trim()), 250);
      });
    }

    async search(term) {
      if (term.length < 2) {
        this.results.innerHTML = '';
        this.results.hidden = true;
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,article',
        'resources[limit]': 6,
        'section_id': 'predictive-search'
      });

      try {
        const response = await fetch(
          window.theme.routes.predictiveSearch + '?' + params.toString(),
          { signal: this.controller.signal }
        );
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const source = html.querySelector('[data-search-results]');
        this.results.innerHTML = source ? source.innerHTML : '';
        this.results.hidden = false;
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.results.hidden = true;
        }
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);

  /* --- Motion --------------------------------------------------------------
     Everything below is suppressed when the visitor asks for reduced motion.
  ------------------------------------------------------------------------- */

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Reveal on scroll -------------------------------------------------------- */

  // Inside the theme editor sections are swapped in and out constantly, and a
  // half-played entrance animation reads as a broken section. Show everything.
  const isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

  function initReveal(root) {
    const scope = root || document;
    const targets = scope.querySelectorAll('.reveal:not(.is-visible)');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window) || prefersReducedMotion.matches || isDesignMode) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    // Children of a staggered container come in one after another rather than
    // all at once — the difference between a page that lands and a page that
    // arrives.
    scope.querySelectorAll('[data-stagger]').forEach((group) => {
      Array.from(group.children).forEach((child, index) => {
        const step = parseInt(group.dataset.stagger, 10) || 90;
        child.style.setProperty('--reveal-delay', index * step + 'ms');
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    targets.forEach((el) => {
      // Anything already on screen is shown at once — waiting for a scroll
      // that may never come is how content ends up invisible.
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        el.classList.add('is-visible');
        return;
      }
      observer.observe(el);
    });

    // Last resort: whatever the reason, nothing stays hidden for long.
    setTimeout(() => {
      targets.forEach((el) => el.classList.add('is-visible'));
    }, 2500);
  }

  /* Parallax ----------------------------------------------------------------
     A slow counter-drift on large photographs. Small movement on purpose —
     the point is that the page feels alive, not that it shows off.
  ------------------------------------------------------------------------- */

  function initParallax() {
    if (prefersReducedMotion.matches) return;
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    const layers = Array.from(document.querySelectorAll('[data-parallax]'));
    if (!layers.length) return;

    let visible = [];
    let ticking = false;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = entry.target;
          if (entry.isIntersecting) {
            if (visible.indexOf(target) === -1) visible.push(target);
          } else {
            visible = visible.filter((el) => el !== target);
          }
        });
        update();
      },
      { rootMargin: '10% 0px' }
    );

    layers.forEach((layer) => observer.observe(layer));

    function update() {
      ticking = false;
      const viewport = window.innerHeight;

      visible.forEach((layer) => {
        const rect = layer.getBoundingClientRect();
        const strength = parseFloat(layer.dataset.parallax) || 12;
        // -1 when the element sits below the fold, 1 when it has passed above.
        const progress = (rect.top + rect.height / 2 - viewport / 2) / viewport;
        const shift = Math.max(-1, Math.min(1, progress)) * strength * -1;
        layer.style.setProperty('--parallax-y', shift.toFixed(2) + '%');
      });
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* Counting numbers -------------------------------------------------------- */

  function initCounters(root) {
    const targets = (root || document).querySelectorAll('[data-count-to]:not(.is-counted)');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window) || prefersReducedMotion.matches) {
      targets.forEach((el) => el.classList.add('is-counted'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          observer.unobserve(el);
          el.classList.add('is-counted');

          const target = parseFloat(el.dataset.countTo);
          if (isNaN(target)) return;

          const duration = 1100;
          const start = performance.now();

          function tick(now) {
            const t = Math.min(1, (now - start) / duration);
            // Ease out so the number settles rather than stopping dead.
            const eased = 1 - Math.pow(1 - t, 3);
            el.textContent = Math.round(target * eased);
            if (t < 1) requestAnimationFrame(tick);
          }

          el.textContent = '0';
          requestAnimationFrame(tick);
        });
      },
      { threshold: 0.4 }
    );

    targets.forEach((el) => observer.observe(el));
  }

  document.addEventListener('DOMContentLoaded', () => {
    initReveal();
    initParallax();
    initCounters();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initReveal(event.target);
    initParallax();
    initCounters(event.target);
  });

  /* --- Copy link ----------------------------------------------------------- */

  document.addEventListener('click', async function (event) {
    const button = event.target.closest('[data-copy-link]');
    if (!button) return;
    event.preventDefault();
    try {
      await navigator.clipboard.writeText(button.dataset.copyLink || window.location.href);
      const label = button.querySelector('[data-copy-label]');
      if (!label) return;
      const previous = label.textContent;
      label.textContent = window.theme.strings.copied;
      setTimeout(() => {
        label.textContent = previous;
      }, 2000);
    } catch (error) {
      /* Clipboard unavailable — the link is still visible to copy by hand. */
    }
  });
})();
